<template>
    <app-chart-wrapper>
        <div class="header">
            <h5 class="heading">
                {{ heading }}
                <i
                    class="mdi mdi-help-circle-outline pointer"
                    v-if="tooltip"
                    v-tooltip="{ content: tooltip }"
                ></i>
            </h5>

            <div class="header-totals" v-if="showTotal && totalItems">
                <template v-for="(total, index) in totalItems">
                    <p :key="index" v-if="totalLabels[index]" class="total">
                        {{ totalLabels[index] }}: {{ totalItems[index] }}
                    </p>
                </template>
            </div>
        </div>

        <div class="data">
            <Loading v-if="loading" />
            <canvas ref="chart"></canvas>
        </div>

        <template v-if="data && isDoughnut">
            <div class="hr"></div>
            <app-doughnut-chart-footer :data-name="name" />
        </template>

        <footer v-if="withHelperText && helperText" class="text-helper">
            <p>{{ helperText }}</p>
        </footer>
    </app-chart-wrapper>
</template>

<script>
import Loading from "@/components/generic/Loading.vue";
import AppDoughnutChartFooter from "./DoughnutChartFooter";
import AppChartWrapper from "./ChartWrapper";
import colors from "@packages/colors";
import Chart from "chart.js";
// import loadsOnFilterUpdate from '@mixins/loads-on-filter-update'
import chartProps from "./mixins/chartProps";
import _ from "lodash";
import provider from "@mixins/provider";

export default {
    components: {
        AppDoughnutChartFooter,
        AppChartWrapper,
        Loading,
    },

    data() {
        return {
            chart: null,
            total: null,
            gridColor: colors.hex.rgba(colors.grey2, 0.45),
        };
    },

    props: {
        dataType: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        showTotal: {
            type: Boolean,
            default: false,
        },
        totalLabels: {
            type: Array,
            default: () => {
                return ["Total"];
            },
        },
        maintainAspectRatio: {
            type: Boolean,
            default: false,
        },
        withHelperText: {
            type: Boolean,
            default: false,
        },
        helperText: {
            type: String,
            default: "",
        },
    },

    computed: {
        chartType() {
            return this.dataType;
        },

        options() {
            if (this.isDoughnut)
                return {
                    cutoutPercentage: this.isVivoProvider ? 60 : 80,
                };

            return {
                elements: {
                    point: {
                        radius: 5,
                        borderWidth: 3,
                    },
                },
                tooltips: {
                    callbacks: {
                        label: (tooltipItem, data) => {
                            const label =
                                data.datasets[tooltipItem.datasetIndex].label ||
                                "";
                            const value = tooltipItem.value || 0;

                            const percentage = (
                                (value /
                                    this.totalCount[tooltipItem.datasetIndex]) *
                                100
                            ).toFixed(0);

                            return `${label}: ${new Intl.NumberFormat(
                                "pt-BR"
                            ).format(value)} (${percentage}%)`;
                        },
                    },
                },
            };
        },

        isDoughnut() {
            return this.chartType === "doughnut";
        },

        data() {
            return this.$store.state.statistics[this.name];
        },

        totalCount() {
            const data = this.$store.state.statistics[this.name];

            if (data) {
                const countIsArray = Array.isArray(data.count);

                if (countIsArray) {
                    return data.count;
                } else {
                    return [data.count];
                }
            }
            return 0;
        },

        totalItems() {
            const data = this.$store.state.statistics[this.name];
            if (data) {
                const countIsArray = Array.isArray(data.count);

                if (countIsArray) {
                    return data.count.map((count) =>
                        new Intl.NumberFormat("pt-BR").format(count)
                    );
                } else {
                    return [
                        new Intl.NumberFormat("pt-BR").format(data.count || 0),
                    ];
                }
            }
            return [0];
        },

        scales() {
            if (this.chartType === "doughnut") return {};

            //todo set type to time

            return {
                xAxes: [
                    {
                        gridLines: {
                            color: this.gridColor,
                            lineWidth: 0.5,
                        },
                        ticks: {
                            maxRotation: 45,
                        },
                    },
                ],

                yAxes: [
                    {
                        gridLines: {
                            color: this.gridColor,
                            lineWidth: 0.5,
                        },
                        ticks: {
                            callback: function (value) {
                                return new Intl.NumberFormat("pt-BR").format(
                                    value
                                );
                            },
                            stepSize: 50,
                            beginAtZero: true,
                            min: 0,
                            maxTicksLimit: 20,
                        },
                    },
                ],
            };
        },

        legend() {
            if (this.chartType !== "line")
                return {
                    display: false,
                };

            return {
                position: "bottom",
                labels: {
                    usePointStyle: true,
                    padding: 24,
                },
            };
        },

        filters() {
            return this.$store.state.statistics.filters;
        },
    },

    watch: {
        async data(data) {
            if (this.chart) await this.chart.destroy();
            this.data.total = data.total;
            data = this[this.chartType + "ChartStyling"](data);

            this.chart = await new Chart(this.$refs.chart, {
                type: this.chartType,
                options: {
                    ...this.options,
                    legend: this.legend,
                    scales: this.scales,
                    responsive: true,
                    maintainAspectRatio: this.maintainAspectRatio,
                },
                data,
            });
        },
    },

    methods: {
        whitelabelCharts() {
            return this.$store.getters["accountWhitelabel/chartColors"];
        },

        lineChartStyling(data) {
            const whitelabelCharts = this.whitelabelCharts();

            _.forEach(data.datasets, (set, index) => {
                const colorLine = whitelabelCharts
                    ? whitelabelCharts[index % whitelabelCharts.length]
                    : {
                        vivo: colors.vivo.charts.line[index],
                        unifique: colors.unifique.charts.line[index],
                        abc: colors.abc.charts.line[index],
                        vero: colors.vero.charts.line[index],
                        wifeed: colors.charts.line[index],
                        wcs: colors.charts.line[index],
                        haggi: colors.charts.line[index],
                        tely: colors.charts.line[index],
                        ntcorp: colors.charts.line[index],
                        "data-carrier": colors.charts.line[index],
                    }[this.savedProvider] || colors.charts.line[index];

                const color = colorLine;

                const gradient = this.gradient(color);

                const styles = {
                    borderColor: color,
                    backgroundColor: gradient,
                    pointBorderColor: colors.hex.rgba(color, 1),
                    pointBackgroundColor: "white",
                    pointHitRadius: 5,
                    radius: 5,
                };

                data.datasets[index] = { ...set, ...styles };
            });

            return data;
        },

        doughnutChartStyling(data) {
            const backgroundColor =
                this.whitelabelCharts() || {
                    vivo: colors.vivo.charts.doughnut,
                    unifique: colors.unifique.charts.doughnut,
                    abc: colors.abc.charts.doughnut,
                    vero: colors.vero.charts.doughnut,
                    wifeed: colors.charts.doughnut,
                    wcs: colors.charts.doughnut,
                    haggi: colors.charts.doughnut,
                    tely: colors.charts.doughnut,
                    ntcorp: colors.charts.doughnut,
                    "data-carrier": colors.charts.doughnut,
                }[this.savedProvider] || colors.charts.doughnut;

            data.datasets[0].backgroundColor = backgroundColor;

            return data;
        },

        barChartStyling(data) {
            const backgroundColor =
                this.whitelabelCharts() || {
                    vivo: colors.vivo.charts.bar,
                    unifique: colors.unifique.charts.bar,
                    abc: colors.abc.charts.bar,
                    vero: colors.vero.charts.bar,
                    wifeed: colors.charts.bar,
                    wcs: colors.charts.bar,
                    haggi: colors.charts.bar,
                    tely: colors.charts.bar,
                    ntcorp: colors.charts.bar,
                    "data-carrier": colors.charts.bar,
                }[this.savedProvider] || colors.charts.bar;

            data.datasets[0].backgroundColor = backgroundColor[0];

            return data;
        },

        gradient(color, toBottom = true) {
            const ctx = this.$refs.chart.getContext("2d");

            const height = document.documentElement.clientWidth / 7;

            const gradient = ctx.createLinearGradient(
                0,
                toBottom ? 0 : height,
                0,
                toBottom ? height : 0
            );

            gradient.addColorStop(0, colors.hex.rgba(color, 1));
            gradient.addColorStop(0.1, colors.hex.rgba(color, 1));
            gradient.addColorStop(0.4, colors.hex.rgba(color, 0.4));
            gradient.addColorStop(1, colors.hex.rgba(color, 0));

            return gradient;
        },
    },
    mixins: [chartProps, provider],
};
</script>

<style lang="scss" scoped>
.loading {
    margin: 0 auto;
    position: absolute;
    left: 50%;
    transform: translate(-50%, 0);
    top: 50%;
    transform: translateY(-50%);
}

.data {
    position: relative;
    height: max-content;
    min-height: 300px;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 5px;

    .heading {
        margin: 0;
    }

    .header-totals {
        display: flex;
        flex-direction: column;
        align-items: center;

        p {
            font-size: 14px;
            margin-bottom: 0;
            font-weight: 500;
        }
    }
}

.text-helper {
    margin-top: auto;
    padding-top: 32px;
    font-size: 14px;
    text-align: center;

    p {
        margin: 0;
    }
}
</style>
